import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { catchError, tap, throwError } from 'rxjs';
import { HttpErrorService } from '@core/services/http-error.service';
import { markHttpErrorHandled } from '@core/utils/http-error.utils';
import { SessionData } from '@core/models/session.model';
import { HttpErrorKind } from '@core/models/http-error.model';

/** Signale les erreurs du backend sans avaler l’échec ni rejouer la requête. */
export const httpErrorInterceptor: HttpInterceptorFn = (request, next) => {
	const httpErrorService = inject(HttpErrorService);
	const base = new URL('.', document.baseURI);
	const url = new URL(request.url, document.baseURI);
	if (url.origin !== base.origin || !url.pathname.startsWith(base.pathname)) return next(request);
	const isEndpoint = (path: string) => url.pathname === new URL(path, document.baseURI).pathname;
	const authentication = isEndpoint('session/login') || isEndpoint('session/checktoken');
	const isLoginRedirect = (responseUrl: string | null) => {
		if (!responseUrl) return false;
		const target = new URL(responseUrl, document.baseURI);
		const login = new URL('login', document.baseURI);
		return target.origin === login.origin && target.pathname === login.pathname && url.pathname !== login.pathname;
	};
	return next(request).pipe(
		tap((event) => {
			if (!(event instanceof HttpResponse)) return;
			if (isLoginRedirect(event.url) && !authentication && request.responseType === 'json') {
				throw new HttpErrorResponse({ status: 401, url: event.url ?? undefined, error: { message: 'Session expirée' } });
			}
			if (
				isEndpoint('session/status') &&
				(event.body as SessionData | null)?.['success'] === false &&
				window.appConfig?.application?.isLogged
			) {
				httpErrorService.report('session');
			}
		}),
		catchError((error: unknown) => {
			if (!(error instanceof HttpErrorResponse)) return throwError(() => error);
			let kind: HttpErrorKind | null = null;
			if (!authentication && (error.status === 401 || isLoginRedirect(error.url))) kind = 'session';
			else if (error.status === 403) {
				const body = typeof error.error === 'string' ? error.error : error.error?.message;
				kind =
					typeof body === 'string' && /(?:invalid|missing|expired)\s+csrf|csrf\s+(?:token|jeton)/i.test(body)
						? 'csrf'
						: 'forbidden';
			} else if (error.status === 0) kind = 'network';
			else if (error.status >= 500) kind = 'server';
			else if (error.status >= 200 && error.status < 300) kind = 'response';
			if (kind) {
				markHttpErrorHandled(error);
				httpErrorService.report(kind);
			}
			return throwError(() => error);
		}),
	);
};
